import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { collectChronologicalDates } from '../main-section.helpers';
import { ChartDataItem } from './contributing-teams-chart.type';

/**
 * Hook that prepares data for the Contributing Teams chart
 *
 * Transforms the raw initiative data into a format suitable for the bar chart,
 * where each data point contains:
 * - date: The time period label
 * - [teamId]: The value for each selected team
 *
 * @returns {ChartDataItem[] | []} An array of data points for the chart, where each item
 * contains a date and values for each selected team
 */
const useChartData = (): ChartDataItem[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const selectedTeams = useThroughputTabStore((state) => state.selectedTeams);
  const metric = useThroughputTabStore((state) => state.metric);

  const chartData = useMemo(() => {
    try {
      if (!initiative) {
        return [];
      }

      const dates = collectChronologicalDates(initiative.contributing_teams_over_time, metric);

      const data = dates.map((date) => {
        const dataEntries = selectedTeams.map((team) => {
          return [team.name, initiative.contributing_teams_over_time[team.id][metric][date]];
        });

        return {
          date,
          ...Object.fromEntries(dataEntries),
        };
      });

      return data;
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }, [initiative, selectedTeams, metric]);

  return chartData;
};

export { useChartData };
